import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, hasFirebaseConfig } from "../config/firebase";
import { portfolioFallback } from "../data/portfolioFallback";

const PortfolioDataContext = createContext({ data: portfolioFallback, source: "local", loading: false });
const arrayKeys = ["aboutStats", "services", "projects", "experience", "skillGroups", "blogPosts", "testimonials", "contactOptions"];
const imageKeys = ["hero", "about", "experience", "blog", "contact"];
const isPublicHttpsUrl = (value) => typeof value === "string" && /^https:\/\//i.test(value);

const isCompleteRemoteContent = (remote) => Boolean(
  remote &&
  remote.profile &&
  typeof remote.profile.name === "string" &&
  isPublicHttpsUrl(remote.profile.resume) &&
  remote.profile.images &&
  imageKeys.every((key) => isPublicHttpsUrl(remote.profile.images[key])) &&
  arrayKeys.every((key) => Array.isArray(remote[key]) && remote[key].length > 0)
);

export const PortfolioDataProvider = ({ children }) => {
  const [state, setState] = useState({ data: portfolioFallback, source: "local", loading: hasFirebaseConfig });

  useEffect(() => {
    let active = true;
    if (!hasFirebaseConfig) return undefined;

    getDoc(doc(db, "portfolio", "siteContent"))
      .then((snapshot) => {
        if (!active) return;
        if (!snapshot.exists()) throw new Error("Portfolio content document does not exist");
        const remoteContent = snapshot.data();
        if (!isCompleteRemoteContent(remoteContent)) throw new Error("Portfolio content document is incomplete");
        setState({ data: remoteContent, source: "firebase", loading: false });
      })
      .catch(() => {
        if (active) setState({ data: portfolioFallback, source: "local", loading: false });
      });

    return () => { active = false; };
  }, []);

  const value = useMemo(() => state, [state]);
  return <PortfolioDataContext.Provider value={value}>{children}</PortfolioDataContext.Provider>;
};

export const usePortfolioData = () => useContext(PortfolioDataContext);
